import { axiosClient } from '../../library/Service/axiosClient';

/**
 * Deduplicates a raw program array.
 *
 * Dedup key rules:
 *   - If programLevel is a number 1–12  → key = "level:<programLevel>"
 *   - Otherwise                         → key = "name:<program.name.toLowerCase().trim()>"
 * First occurrence of each key is kept; subsequent duplicates are dropped.
 */
export function dedupPrograms(programs: any[]): any[] {
    if (!Array.isArray(programs)) return [];
    const seen = new Set<string>();
    return programs.filter((p) => {
        const level = Number(p?.programLevel);
        const key =
            Number.isInteger(level) && level >= 1 && level <= 12
                ? `level:${level}`
                : `name:${String(p?.name ?? '').toLowerCase().trim()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/**
 * Formats a (possibly deduped) program array into Select-compatible options.
 * Prefers `displayName` over `name` for the label.
 */
export function formatProgramOptions(programs: any[]): { label: string; value: number }[] {
    if (!Array.isArray(programs)) return [];
    return programs.map((p) => ({
        label: p?.displayName || p?.name || '',
        value: Number(p?.id),
    }));
}

/**
 * Converts the { label, value } program options into the DomainElement-compatible
 * { code, value } shape required by PageBuilder's SelectControl.
 *
 * SelectControl renders: <option value={domain.code}>{domain.value}</option>
 * so `code` must carry the numeric ID (as string) and `value` must be the display name.
 */
export function toProgramDomainElements(
    programOptions: { label: string; value: number }[]
): { categoryCode: string; code: string; value: string; parentCode: string; displayOrder: number }[] {
    return programOptions.map((opt, index) => ({
        categoryCode: 'PROGRAM_LIST',
        code: String(opt.value),
        value: opt.label,
        parentCode: '',
        displayOrder: index + 1,
    }));
}

/**
 * Fetches program options for a given institute or institute group.
 *
 * - If `instituteId` is provided  → POST { instituteId }  — no dedup (single campus).
 * - If only `groupId` is provided → POST { groupId }      — dedup applied (multi-campus).
 *
 * Returns [] on any error so callers can handle an empty dropdown gracefully.
 */
export async function fetchProgramOptions(
    instituteId: string | null | undefined,
    instituteGroupId: string | null | undefined
): Promise<{ label: string; value: number }[]> {
    try {
        const body = instituteId ? { instituteId } : { groupId: instituteGroupId };
        const response = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/program/get-program-details`,
            body
        );
        const programs: any[] = Array.isArray(response?.data) ? response.data : [];
        const processed = instituteId ? programs : dedupPrograms(programs);
        return formatProgramOptions(processed);
    } catch {
        return [];
    }
}
